import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EventRound, EventTeam, HitsterEvent } from '../types';
import ScoreSheetPages, { RowChecks, TeamChecks, createEmptyChecks } from '../components/print/ScoreSheetPages';
import './PrintScoreSheet.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const PrintScoreSheet: React.FC = () => {
  const { eventId, roundId } = useParams<{ eventId: string; roundId: string }>();
  const navigate = useNavigate();

  const [event, setEvent] = useState<HitsterEvent | null>(null);
  const [round, setRound] = useState<EventRound | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [teamChecks, setTeamChecks] = useState<Record<string, TeamChecks>>({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/events/${eventId}`);
        if (response.ok) {
          const data: HitsterEvent = await response.json();
          setEvent(data);
          const r = data.rounds.find((x: EventRound) => x.id === roundId);
          setRound(r || null);
          if (data.teams) {
            setSelectedTeamIds(data.teams.map((t: EventTeam) => t.id));
          }
        }
      } catch (error) {
        console.error('Failed to load event for score sheet:', error);
      }
      setLoading(false);
    };
    loadData();
  }, [eventId, roundId]);

  const handlePrint = () => window.print();

  const toggleTeam = (teamId: string) => {
    setSelectedTeamIds(prev =>
      prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]
    );
  };

  const toggleCheck = (teamId: string, rowKey: string, field: keyof RowChecks) => {
    setTeamChecks(prev => ({
      ...prev,
      [teamId]: {
        ...prev[teamId],
        [rowKey]: {
          ...(prev[teamId]?.[rowKey] || createEmptyChecks()),
          [field]: !(prev[teamId]?.[rowKey]?.[field] || false),
        },
      },
    }));
  };

  if (loading) return <div className="print-score-sheet loading">Laden...</div>;
  if (!event || !round) return <div className="print-score-sheet error">Ronde niet gevonden</div>;

  const teams = (event.teams || []).filter(t => selectedTeamIds.includes(t.id));

  return (
    <div className="print-score-sheet">
      <div className="score-sheet-controls no-print">
        <button className="back-btn" onClick={() => navigate(`/round-scoring/${eventId}/${roundId}`)}>
          ← Terug
        </button>
        <h1>📋 Scoreformulier — {round.name}</h1>
        <div className="team-selector">
          <span>Teams tonen:</span>
          {(event.teams || []).map(team => (
            <label key={team.id} className="team-checkbox">
              <input
                type="checkbox"
                checked={selectedTeamIds.includes(team.id)}
                onChange={() => toggleTeam(team.id)}
              />
              {team.name}
            </label>
          ))}
        </div>
        <button className="print-btn" onClick={handlePrint}>🖨️ Afdrukken</button>
        <div className="print-orientation-note">
          Dit blad is ingesteld voor liggend A4.
        </div>
      </div>

      {teams.length > 0 && (
        <ScoreSheetPages
          event={event}
          rounds={[round]}
          teams={teams}
          teamChecks={teamChecks}
          onToggleCheck={toggleCheck}
        />
      )}

      {teams.length === 0 && (
        <div className="no-teams no-print">Geen teams geselecteerd</div>
      )}
    </div>
  );
};

export default PrintScoreSheet;
